import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import QrCode from '@/components/QrCode'
import { clearNsecRecord, saveNsecModalAtom, TSaveNsecModalMode } from '@/atoms/key-backup'
import { accountAtom, logout } from '@/atoms/session'
import { HugeiconsIcon } from '@hugeicons/react'
import { DownloadIcon, EyeIcon, EyeOffIcon, Loading03Icon } from '@hugeicons/core-free-icons'
import * as nip19 from '@nostr/tools/nip19'
import * as nip49 from '@nostr/tools/nip49'
import { useAtomValue, useSetAtom } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export default function SaveNsecDialog() {
  const { t } = useTranslation()
  const modal = useAtomValue(saveNsecModalAtom)
  const setModal = useSetAtom(saveNsecModalAtom)
  const account = useAtomValue(accountAtom)

  const [revealed, setRevealed] = useState(false)
  const [password, setPassword] = useState('')
  const [ncryptsec, setNcryptsec] = useState<string | null>(null)
  const [computing, setComputing] = useState(false)

  const nsec = account?.signerType === 'nsec' ? account.nsec : undefined

  const privkey = useMemo(() => {
    if (!nsec) return null
    try {
      const { type, data } = nip19.decode(nsec)
      if (type !== 'nsec') return null
      return data as Uint8Array
    } catch {
      return null
    }
  }, [nsec])

  useEffect(() => {
    if (!modal.open) return
    setRevealed(false)
    setPassword('')
    setNcryptsec(null)
    setComputing(false)
  }, [modal.open])

  useEffect(() => {
    if (!privkey || !password) {
      setNcryptsec(null)
      return
    }
    setComputing(true)
    const timer = setTimeout(() => {
      try {
        setNcryptsec(nip49.encrypt(privkey, password))
      } catch {
        setNcryptsec(null)
      }
      setComputing(false)
    }, 600)
    return () => clearTimeout(timer)
  }, [password, privkey])

  if (!nsec || !account) return null

  const closeModal = () => setModal({ open: false, mode: 'normal' })

  // closing without clicking any button keeps the record intact
  const onOpenChange = (open: boolean) => {
    if (!open) closeModal()
  }

  const qrValue = ncryptsec ?? (revealed ? nsec : null)

  const downloadNcryptsec = () => {
    if (!ncryptsec) return
    const blob = new Blob([ncryptsec], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${nip19.npubEncode(account.pubkey)}-ncryptsec.txt`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
    clearNsecRecord()
    closeModal()
  }

  const savedIt = () => {
    clearNsecRecord()
    closeModal()
  }

  const logoutAndLoseKeyForever = () => {
    clearNsecRecord()
    closeModal()
    logout()
  }

  const mode: TSaveNsecModalMode = modal.mode

  return (
    <Dialog open={modal.open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('Save your private key')}</DialogTitle>
          <DialogDescription>
            {t(
              'If you lose this key you lose access to your profile forever. Save it somewhere safe.'
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="grid gap-2">
            <Label>{t('Your private key')}</Label>
            <div className="flex gap-2">
              <Input
                readOnly
                type={revealed ? 'text' : 'password'}
                value={nsec}
                className="font-mono text-xs"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => setRevealed((r) => !r)}
                title={revealed ? t('Hide') : t('Click to reveal')}
              >
                <HugeiconsIcon icon={revealed ? EyeOffIcon : EyeIcon} />
              </Button>
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="backup-password">{t('Or encrypt it with a password (NIP-49)')}</Label>
            <div className="flex gap-2">
              <Input
                id="backup-password"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('Choose a password')}
              />
              <Button onClick={downloadNcryptsec} disabled={!ncryptsec}>
                {computing ? (
                  <HugeiconsIcon icon={Loading03Icon} className="animate-spin" />
                ) : (
                  <HugeiconsIcon icon={DownloadIcon} />
                )}
                {t('Download')}
              </Button>
            </div>
          </div>
          {ncryptsec && (
            <div className="grid gap-2">
              <Label htmlFor="generated-ncryptsec">{t('Your encrypted private key')}</Label>
              <Input
                id="generated-ncryptsec"
                readOnly
                value={ncryptsec}
                className="font-mono text-xs"
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
          )}
          {qrValue && (
            <div className="flex justify-center">
              <QrCode value={qrValue} size={220} />
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          {mode === 'logout' ? (
            <>
              <Button variant="outline" onClick={savedIt} className="w-full sm:w-auto">
                {t("I've saved it")}
              </Button>
              <Button variant="destructive" onClick={logoutAndLoseKeyForever}>
                {t('Logout and lose my key forever')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={closeModal}>
                {t('Later')}
              </Button>
              <Button onClick={savedIt}>{t("I've saved it")}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
